/**
 * The editor's side of bringing an SVG in as layers.
 *
 * The engine reads the file (`svgToArtwork` in flobi-artwork-core) and hands back an op
 * batch; what it cannot do from there is anything that needs a browser or the network —
 * fetching the pictures a file embeds so they can be uploaded, deciding how big the
 * drawing should land for the view it lands in, and saying what was left out in a
 * sentence short enough for a notice. Those live here, apart from the canvas, so they
 * can be tested without one.
 */

/** The name a paste of SVG code is given, so the layer can be named after its own title. */
export const PASTED_SVG = 'pasted.svg';

/** An SVG by its type, or by its name when the system gave it no type (it happens on drop). */
export function isSvgFile(file: File): boolean {
  return file.type === 'image/svg+xml' || (!file.type && /\.svg$/i.test(file.name));
}

/** The top group's name: the file's, without the extension. A paste has none of its own. */
export function svgLayerName(file: File): string | undefined {
  if (file.name === PASTED_SVG) return undefined;
  return file.name.replace(/\.svgz?$/i, '').trim() || undefined;
}

/**
 * How much to shrink a drawing so it lands inside the view, as a picture does: no more
 * than six tenths of the view across or down. Never enlarged — an icon comes in the size
 * it was drawn.
 */
export function fitScale(width: number, height: number, view: { width: number; height: number }, zoom: number): number {
  const room = Math.min((view.width * 0.6) / zoom / Math.max(1, width), (view.height * 0.6) / zoom / Math.max(1, height));
  return Number.isFinite(room) && room > 0 ? Math.min(1, room) : 1;
}

/**
 * A picture the SVG embeds, as a file that can be uploaded. A data: URI is decoded here;
 * a web address is fetched, which only works where its server allows it. Anything else
 * (a path relative to wherever the SVG once lived) cannot be found and is null.
 */
export async function pictureFile(href: string, index: number): Promise<File | null> {
  if (!/^(data:|https?:)/i.test(href)) return null;
  try {
    const response = await fetch(href);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return null;
    const extension = blob.type.split('/')[1]?.split('+')[0] ?? 'png';
    return new File([blob], `svg-picture-${index + 1}.${extension}`, { type: blob.type });
  } catch {
    return null;
  }
}

/** The engine's notes as one notice: the first two said, the rest counted. */
export function svgNotice(notes: readonly string[]): string | null {
  if (notes.length === 0) return null;
  if (notes.length <= 2) return notes.join(' ');
  const more = notes.length - 2;
  return `${notes.slice(0, 2).join(' ')} ${more === 1 ? 'One more thing' : `${more} more things`} came in differently.`;
}
